import { useAuth } from '@clerk/react';
import { isTestUser, isVrpMember } from '@vrplatform/api';
import { hasValue, isUUID } from '@vrplatform/utils';
import * as Sentry from '@sentry/react';
import type {
  TrackingEvent as SharedTrackingEvent,
  TrackingEventProps,
} from '@vrplatform/tracking';
import { formatUserName } from '@vrplatform/ui-common';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';
import {
  GRAPHQL_URI,
  isExtension,
  isLocalhost,
  isProductionEnvironment,
  isStorybook,
  isStagingEnvironment,
} from '../env';
import { useApiMutation } from '../api';
import { useIsOwnerPortalPreview } from './owner';
import { captureSentryError } from './sentry';
import {
  type TeamType,
  type UserRole,
  readOwnerPortalPreview,
  useMe,
  useTeam,
} from './team';
import { IntercomProvider, useIntercom } from './useIntercom';
import {
  createOwnedSessionReplayController,
  getBirdieContact,
  getSentryReplaySessionSampleRate,
  getSentryTraceSampleRate,
  SENTRY_PORTAL_STORAGE_KEY,
  getSentryTunnelUrl,
  isSentryAutomationSession,
  isTrackingEnabled,
  shouldCreateSentryRequestSpan,
  shouldDropSentryEvent,
  shouldEnableTargetedMixpanelReplay,
  shouldInitializeSentryReplay,
  trackingEventErrorCapturePolicy,
  withUnhandledApiRejectionFingerprint,
  withMaximumUpdateDepthFingerprint,
} from './useTracking.utils';
import { isPlaywrightUserEmail } from './isPlaywrightUserEmail';

const TRACKING_ENABLED = isTrackingEnabled({
  isExtension,
  isLocalhost,
  isStorybook,
});
const SUPPRESSED_TRACKING_EVENT = async () => {};
const SENTRY_AUTOMATION_SESSION = isSentryAutomationSession({
  isWebdriver: typeof navigator !== 'undefined' && navigator.webdriver,
  search: typeof window === 'undefined' ? '' : window.location.search,
});
// Evaluated when Sentry initialises, after a customer portal bootstrap may
// have marked the environment as production.
const isSentryReplayEnabled = () =>
  shouldInitializeSentryReplay({
    isAutomationSession: SENTRY_AUTOMATION_SESSION,
    isExtension,
    isLocalhost,
    isProduction: isProductionEnvironment(),
    isStaging: isStagingEnvironment(),
    isStorybook,
    pathname: typeof window === 'undefined' ? '' : window.location.pathname,
    productionEnabled: true,
    stagingEnabled: true,
  });
const SENTRY_APPLICATION_KEY = 'vrplatform-ui';

type TrackingEventMetadata = {
  userId?: string;
  tenantId?: string;
  userEmail?: string;
};
type TrackingEventCallback = <TEvent extends SharedTrackingEvent>(
  event: TEvent,
  data: TrackingEventMetadata & TrackingEventProps[TEvent]
) => Promise<void>;

export type Tracking = ReturnType<typeof useTrackingEvent>;
export type { TrackingEvent } from '@vrplatform/tracking';

let isSentryInitialized = false;
// @birdie-so/snippet loads lazily to stay out of the initial JS budget. It is
// initialized once; `updateBirdie` calls wait on `loadBirdie`.
let birdieLoading: Promise<typeof import('@birdie-so/snippet')> | undefined;
// mixpanel-browser loads lazily: every SDK call waits on `loadMixpanel`, so calls
// made before the chunk arrives are queued instead of lost.
type MixpanelSdk = (typeof import('mixpanel-browser'))['default'];
let mixpanel: MixpanelSdk | undefined;
let mixpanelLoading: Promise<MixpanelSdk> | undefined;
let resolveMixpanelReady: () => void;
const mixpanelReady = new Promise<void>((resolve) => {
  resolveMixpanelReady = resolve;
});
const mixpanelReplayController = createOwnedSessionReplayController({
  getActiveReplayId: () => {
    if (!mixpanel) return undefined;
    const properties = mixpanel.get_session_recording_properties();
    return '$mp_replay_id' in properties ? properties.$mp_replay_id : undefined;
  },
  prepare: () => loadMixpanel().then(() => mixpanelReady),
  hasOptedOutTracking: () => !!mixpanel?.has_opted_out_tracking(),
  onError: (error, operation) => {
    captureSentryError(error, {
      tags: { operation: `mixpanel-targeted-session-replay-${operation}` },
    });
  },
  startRecording: () => mixpanel?.start_session_recording(),
  stopRecording: () => mixpanel?.stop_session_recording(),
});

function initSentryOnce() {
  if (!TRACKING_ENABLED || isSentryInitialized) return;
  isSentryInitialized = true;
  const production = isProductionEnvironment();
  const replayEnabled = isSentryReplayEnabled();

  Sentry.init({
    dsn: 'https://4ba9b6d2701d4a889e6f323c050a4a46@o515442.ingest.sentry.io/5624987',
    sampleRate: SENTRY_AUTOMATION_SESSION ? 0.1 : 1,
    initialScope: SENTRY_AUTOMATION_SESSION
      ? { tags: { synthetic_test: 'playwright' } }
      : undefined,
    replaysSessionSampleRate: replayEnabled
      ? getSentryReplaySessionSampleRate(
          import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE
        )
      : 0,
    replaysOnErrorSampleRate: replayEnabled ? 1 : 0,
    release:
      document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')
        ?.content || undefined,
    environment: import.meta.env.DEV
      ? 'development'
      : production
        ? 'production'
        : 'staging',
    tracesSampler: ({ inheritOrSampleWith }) =>
      getSentryTraceSampleRate({
        inheritOrSampleWith,
        isProduction: production,
        isOwnerPortal:
          globalThis.localStorage?.getItem(SENTRY_PORTAL_STORAGE_KEY) ===
          'owner',
      }),
    ignoreSpans: [{ op: /^resource\.(?:script|link|other)$/ }],
    tunnel: getSentryTunnelUrl(window.location.hostname),
    tracePropagationTargets: [
      'localhost:8877',
      'api.vrplatform.app',
      'staging.api.vrplatform.app',
      'staging.api.edge.vrplatform.app',
      // Match this origin only; a hostname substring also matches Clerk satellites.
      new RegExp(
        `^${window.location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/`
      ),
    ],
    integrations: [
      Sentry.graphqlClientIntegration({
        endpoints: [GRAPHQL_URI],
      }),
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        // The default 15 s ends a pageload or navigation with `cancelled` and
        // cancels its open children first. `view.ready` times out at 20 s.
        childSpanTimeout: 25_000,
        shouldCreateSpanForRequest: (requestUrl) =>
          shouldCreateSentryRequestSpan({
            currentOrigin: window.location.origin,
            graphqlUrl: GRAPHQL_URI,
            requestUrl,
          }),
      }),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [SENTRY_APPLICATION_KEY],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
        ignoreSentryInternalFrames: true,
      }),
    ].filter(hasValue),
    beforeSend: (event, hint) => {
      if (
        shouldDropSentryEvent({
          event,
          hint,
        })
      ) {
        return null;
      }

      return withMaximumUpdateDepthFingerprint(
        withUnhandledApiRejectionFingerprint(event, hint)
      );
    },
  });

  // Replay and feedback are ~45 KB gzipped, so they load after the page has
  // painted. `Sentry.addIntegration` applies the sample rates set in `init`.
  const addLazyIntegrations = () => {
    import('./useTracking.sentry-integrations')
      .then((module) => module.addSentryReplayAndFeedback(replayEnabled))
      .catch((error: unknown) =>
        console.debug('Sentry replay and feedback failed to load:', error)
      );
  };
  if (document.readyState === 'complete') addLazyIntegrations();
  else window.addEventListener('load', addLazyIntegrations, { once: true });
}

// Mixpanel loads two seconds after navigation start, after first paint. Calls
// before then wait for it; a failed load is retried by the next call.
function loadMixpanel() {
  mixpanelLoading ??= new Promise((resolve) =>
    setTimeout(resolve, Math.max(0, 2000 - performance.now()))
  )
    .then(() => import('mixpanel-browser'))
    .then(({ default: sdk }) => {
      initMixpanel(sdk);
      return sdk;
    })
    .catch((error: unknown) => {
      mixpanelLoading = undefined;
      throw error;
    });
  return mixpanelLoading;
}

function initMixpanel(sdk: MixpanelSdk) {
  mixpanel = sdk;
  sdk.init('933a19cf6d01a51a06ccdd083083f2a9', {
    api_host: 'https://api-eu.mixpanel.com',
    // Shares the anonymous identity with the marketing site on `.vrplatform.app`.
    persistence: 'cookie',
    cross_subdomain_cookie: true,
    record_console: false,
    record_network: false,
    record_sessions_percent: 0,
    // Replays run without consent, so no text or input may be recorded (GRO-1832).
    record_mask_all_inputs: true,
    record_mask_all_text: true,
  });
  // The pinned SDK patch includes recorder startup; tracking opt-out returns undefined.
  const recordingSdk = sdk as typeof sdk & {
    __get_recording_init_promise(): Promise<void> | undefined;
  };
  void Promise.resolve(recordingSdk.__get_recording_init_promise()).then(
    resolveMixpanelReady,
    (error: unknown) => {
      captureSentryError(error, {
        tags: { operation: 'mixpanel-replay-init' },
      });
      resolveMixpanelReady();
    }
  );
}

function loadBirdie() {
  birdieLoading ??= import('@birdie-so/snippet')
    .then((birdie) => {
      birdie.initBirdie({ clientId: 'ayjln8yw' });
      return birdie;
    })
    .catch((error: unknown) => {
      birdieLoading = undefined;
      throw error;
    });
  return birdieLoading;
}

const preventTracking = (
  user: {
    id: string;
    email: string;
  },
  isTestTeam: boolean,
  isActor: boolean
): boolean => {
  return (
    isExtension ||
    isPlaywrightUserEmail(user.email) ||
    isTestTeam ||
    isTestUser({ userEmail: user.email, userId: user.id }) ||
    // isVrpMember({ email: user.email, userId: user.id }) ||
    !!isActor
  );
};

function identifySentry(
  args: {
    user: {
      name: string;
      email: string;
      createdAt: string;
      id: string;
    };
    team:
      | {
          id: string;
          name: string;
          isOnboarding: boolean;
          isGL: boolean;
          type: TeamType;
        }
      | undefined;
  } | null
) {
  Sentry.setUser(
    !args
      ? null
      : {
          name: args.user.name,
          email: args.user.email,
          createdAt: args.user.createdAt,
          id: args.user.id,
        }
  );

  Sentry?.setTags({
    // undefined resets a tag
    tenant_id: args?.team?.id,
    tenant_name: args?.team?.name,
    tenant_type: args?.team?.type,
    product: !args?.team
      ? undefined
      : args?.team.isGL
        ? 'VRTrust'
        : 'VRIntegrations',
  });
}

// this boots intercom, mixpanel and sentry at the top level => RootWrapper
export function TrackingInit({ children }: { children: ReactNode }) {
  // moved initSentryOnce() outside useEffect to satisfy the React Router requirement that the router wrapper (wrapCreateBrowserRouter) runs after Sentry.init. If Sentry initializes later (in an effect), the router gets wrapped before Sentry is ready and you can miss the initial pageload/first navigation. Calling it during render guarantees Sentry is ready before any router is created.
  initSentryOnce();

  useEffect(() => {
    // do not track extension
    if (!TRACKING_ENABLED) return;

    // MIXPANEL: loaded after first paint
    loadMixpanel().catch((error: unknown) =>
      console.debug('Mixpanel failed to load:', error)
    );

    // BIRDIE
    loadBirdie().catch((error: unknown) =>
      console.debug('Birdie failed to load:', error)
    );
  }, []);

  return <IntercomProvider>{children}</IntercomProvider>;
}

export function useTrackingEvent() {
  const [{ id: tenantId, isTest: isTestTeam }] = useTeam();
  const isOwnerPortalPreview = useIsOwnerPortalPreview();
  const { id: meId, email: meEmail } = useMe();
  const { mutateAsync: persistTrackingEvent } = useApiMutation(
    'post',
    '/internal/events'
  );

  const track: TrackingEventCallback = useCallback(
    async (
      event: SharedTrackingEvent,
      data: TrackingEventMetadata & object
    ) => {
      const meta = { userId: meId, tenantId, userEmail: meEmail, ...data };

      if (
        isTestTeam ||
        isPlaywrightUserEmail(meta.userEmail || '') ||
        isTestUser({
          userEmail: meta.userEmail,
          userId: meta.userId,
        }) ||
        isVrpMember({ email: meta.userEmail, userId: meta.userId })
      )
        return;

      if (!meId || !tenantId) return;

      const { userId: _userId, tenantId: _tenantId, ...props } = meta;

      try {
        await persistTrackingEvent({
          body: {
            name: event,
            packageName: 'ui',
            props,
          },
        });
      } catch (err) {
        const captureDecision = trackingEventErrorCapturePolicy.evaluate();

        if (captureDecision.shouldCapture) {
          captureSentryError(err, {
            tags: {
              operation: 'post-internal-events',
              source: 'tracking-event-delivery',
            },
            extra: {
              suppressedCount: captureDecision.suppressedCount,
              windowMs: captureDecision.windowMs,
            },
          });
        }
      }
    },
    [meId, tenantId, meEmail, persistTrackingEvent, isTestTeam]
  );

  if (
    preventTracking(
      { id: meId, email: meEmail },
      isTestTeam,
      isOwnerPortalPreview
    )
  )
    return SUPPRESSED_TRACKING_EVENT;

  return track;
}

export function useTargetedMixpanelReplay() {
  const [{ id: tenantId, isTest: isTestTeam }] = useTeam();
  const isOwnerPortalPreview = useIsOwnerPortalPreview();
  const { id: meId, email: meEmail } = useMe();
  const owner = useRef({});
  const eligible = shouldEnableTargetedMixpanelReplay({
    isActor: isOwnerPortalPreview,
    isExtension,
    tenantId,
    isTestTeam,
    trackingEnabled: TRACKING_ENABLED,
    user: { id: meId, email: meEmail },
  });
  const stop = useCallback(() => {
    mixpanelReplayController.stop(owner.current);
  }, []);
  const start = useCallback(() => {
    if (!eligible) return Promise.resolve(undefined);
    return mixpanelReplayController.start(owner.current);
  }, [eligible]);
  useEffect(() => {
    if (!eligible) stop();
  }, [eligible, stop]);
  return useMemo(() => ({ eligible, start, stop }), [eligible, start, stop]);
}

// GRO-1832: the user's productAnalytics setting from /me. Off opts this browser
// out of Mixpanel analytics and replay and clears Mixpanel's cookie; the stored
// opt-out keeps Mixpanel off from the start of the next visit. Sentry, Birdie
// and Intercom are unaffected.
export function useProductAnalyticsPreference(enabled: boolean | undefined) {
  useEffect(() => {
    if (!TRACKING_ENABLED || enabled === undefined) return;

    loadMixpanel()
      .then((sdk) => {
        if (!enabled) {
          // Opting out also stops a running replay and clears persistence.
          mixpanelReplayController.reset();
          sdk.opt_out_tracking({ delete_user: false });
        } else if (
          sdk.has_opted_out_tracking() &&
          navigator.doNotTrack !== '1'
        ) {
          // Lifts only a stored opt-out; Do Not Track keeps Mixpanel off.
          sdk.opt_in_tracking({ track: () => undefined });
        }
      })
      .catch((error: unknown) =>
        console.debug('Mixpanel failed to load:', error)
      );
  }, [enabled]);
}

export function useTrackingPage() {
  const intercom = useIntercom();

  return useCallback(() => {
    // Runs above the preview session, so it reads the preview tab marker. The
    // open page still carries the single-use preview code in its address.
    if (
      !TRACKING_ENABLED ||
      window.location.pathname === '/owner-preview/open' ||
      readOwnerPortalPreview()
    )
      return;

    loadMixpanel()
      .then((sdk) => sdk.track_pageview())
      .catch((error: unknown) =>
        console.debug('Mixpanel failed to load:', error)
      );
    intercom.update();
    // sentry tracks page view via wrapping <BrowserRouter>
  }, [intercom.update]);
}

export function useTrackingIdentify() {
  const intercom = useIntercom();
  const { actor } = useAuth();
  const [isBooted, setIsBooted] = useState(false);

  return useCallback(
    ({
      canShowVRPilotOnboardingGuide,
      intercomIdentifyId,
      sidekickCutoverEnabled = false,
      team,
      user,
    }: {
      canShowVRPilotOnboardingGuide: boolean;
      intercomIdentifyId: string;
      sidekickCutoverEnabled?: boolean;
      user: {
        id: string;
        email: string;
        avatar: string | undefined;
        name: string;
        firstName: string | undefined;
        lastName: string | undefined;
        createdAt: string;
        lastSeenAt: string | null | undefined;
        country: string | undefined;
        isSuperAdmin: boolean;
        isVrpAdmin: boolean;
        isPartnerAdmin: boolean;
        isTeamAdmin: boolean;
        role: UserRole | undefined;
      };
      team:
        | {
            id: string;
            name: string;
            isOnboarding: boolean;
            isGL: boolean;
            isTest: boolean;
            type: TeamType;
          }
        | undefined;
    }) => {
      if (isBooted) return;

      const trackingPrevented = preventTracking(
        { id: user.id, email: user.email },
        team?.isTest ?? false,
        !!actor
      );

      if (
        !TRACKING_ENABLED ||
        !!actor ||
        (trackingPrevented && !sidekickCutoverEnabled)
      )
        return;

      setIsBooted(true);

      const {
        id: userId,
        email,
        avatar,
        firstName,
        lastName,
        createdAt,
        lastSeenAt,
        ...traits
      } = user;

      const name = formatUserName(
        {
          firstName,
          lastName,
        },
        { lastNameFirst: false, showEmpty: true }
      );

      // MIXPANEL, BIRDIE, SENTRY
      if (!trackingPrevented) {
        const birdieContact = getBirdieContact({
          id: userId,
          email,
          name,
        });

        if (birdieContact) {
          loadBirdie()
            .then((birdie) => birdie.updateBirdie({ contact: birdieContact }))
            .catch((err: unknown) =>
              console.error('Error identifying Birdie:', err)
            );
        }

        loadMixpanel()
          .then((sdk) => {
            sdk.identify(userId);
            sdk.people.set({
              $distinct_id: userId,
              $name: name,
              $first_name: firstName,
              $last_name: lastName,
              $email: email,
              $avatar: avatar,
              $last_seen: lastSeenAt,
              $created: createdAt,
              ...traits,
            });

            if (team) sdk.set_group('tenant', team.id);
          })
          .catch((err: unknown) => {
            console.error('Error identifying mixpanel:', err);
          });

        // SENTRY
        identifySentry({
          user,
          team,
        });
      }

      // Wait for Intercom to boot (max 20 seconds)
      const timeout = setTimeout(() => {
        clearInterval(interval);
        console.warn('Intercom boot timeout');
      }, 20000);

      const interval = setInterval(() => {
        if ((window as any)?.Intercom?.booted) {
          intercom.boot({
            // data attributes
            userId,
            email,
            userHash: intercomIdentifyId,
            // messenger attributes
            hideDefaultLauncher:
              !!team && !team.isOnboarding && !canShowVRPilotOnboardingGuide,
            alignment: 'right',
            horizontalPadding: 40,
            verticalPadding: 40,
            name,
            createdAt,
            avatar: { imageUrl: avatar, type: 'avatar' },
            customAttributes: {
              userType: traits.role,
            },
          });

          // Clear interval after successful boot
          clearInterval(interval);
          clearTimeout(timeout);
        }
      }, 1000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    },
    [intercom.boot, actor, isBooted]
  );
}

export function useTrackingReset() {
  const intercom = useIntercom();

  if (!TRACKING_ENABLED)
    return async (signOut: (() => Promise<void>) | undefined) => {
      if (signOut) await signOut();
    };

  return useCallback(
    async (signOut: (() => Promise<void>) | undefined) => {
      // mixpanel: only an already loaded SDK can hold an identified user
      const identifiedMixpanel =
        mixpanel && isUUID(mixpanel.get_distinct_id()) ? mixpanel : undefined;
      if (identifiedMixpanel) {
        mixpanelReplayController.reset();
        identifiedMixpanel.reset();
      }

      // sentry
      identifySentry(null);

      // only reset intercom if we are signing out
      if (signOut) await signOut();
      intercom.boot({}); // boot intercom without user
    },
    [intercom.boot]
  );
}

export function useTrackingSignOut() {
  const me = useMe();
  const { signOut: clerkSignOut } = useAuth();
  const track = useTrackingEvent();
  const reset = useTrackingReset();

  return useCallback(
    async ({ trackEvent }: { trackEvent: boolean }) => {
      if (TRACKING_ENABLED && trackEvent) {
        await track('account_signed_out', {
          userEmail: me.email,
          lastName: me.lastName,
          firstName: me.firstName,
        })
          .catch((err) => {
            captureSentryError(err);
          })
          .finally(() => {
            reset(clerkSignOut);
          });
      } else await reset(clerkSignOut);
    },
    [clerkSignOut, reset, track, me.email, me.lastName, me.firstName]
  );
}
